import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Modal } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI, useFilterParams } from '../../hooks';
import { taskAPI, employeeAPI } from '../../services';
import { toast } from 'react-toastify';
import moment from 'moment';
import PaginationComponent from '../../components/PaginationComponent';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

const QUICK_DATE_OPTIONS = [
  { label: 'Hôm nay', days: 0 },
  { label: '1 ngày', days: 1 },
  { label: '3 ngày', days: 3 },
  { label: '7 ngày', days: 7 },
  { label: '14 ngày', days: 14 },
  { label: '30 ngày', days: 30 },
];

const TASK_STATUS_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'PENDING', label: 'Chờ xử lý' },
  { value: 'IN_PROGRESS', label: 'Đang làm' },
  { value: 'COMPLETED', label: 'Hoàn thành' },
  { value: 'FAILED', label: 'Thất bại' },
  { value: 'NEED_REVIEW', label: 'Cần review' },
  { value: 'REVIEWED', label: 'Đã review' },
];

/**
 * #191439 — Màn này là ĐÍCH của link sâu trong 4 tin báo cáo tuần/tháng (dòng 177, 178, 179,
 * 180 của bảng khảo sát #132320: cron gửi tự động + API admin gọi tay, cả hai dùng chung hàm
 * dựng tin ở `report.service.ts`). Tin gửi `?from=&to=` khoá đúng kỳ báo cáo.
 *
 * Không có `from`/`to` trên URL thì link mở ra khoảng mặc định 7 ngày gần nhất — với báo cáo
 * THÁNG thì đó là dữ liệu hoàn toàn khác với con số vừa đọc trong tin.
 */
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

const TASK_HISTORY_FILTER_SPEC = {
  employee_id: { default: '' },
  status: { default: '', parse: (v) => v.toUpperCase() },
  from: {
    default: () => moment().subtract(7, 'days').format('YYYY-MM-DD'),
    valid: isIsoDate,
  },
  to: { default: () => moment().format('YYYY-MM-DD'), valid: isIsoDate },
  // #092536 — deep-link từ tin "📝 Giải trình mới — Cần duyệt" của phạt "Task quá hạn"
  // (PointSource.TASK_DEADLINE): `shift_task_id` = id ShiftTask bị phạt, mở THẲNG modal
  // "Chi tiết Task" của đúng bản ghi đó (task nào, hạn lúc nào, ai/khi nào ghi nhận quá hạn).
  // Cùng khuôn với cặp `source_kind`+`source_id` mà #151415 thêm cho màn /qc-results.
  // Cố ý KHÔNG cộng thêm vào bộ lọc danh sách — trang vẫn nạp danh sách 7 ngày mặc định,
  // modal mở riêng nhờ bản ghi tải bằng GET /shift-tasks/:id.
  shift_task_id: { default: '' },
};

export default function TaskHistoryStatsPage() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();

  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(100);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    pending: 0,
    inProgress: 0,
    needReview: 0,
    reviewed: 0,
  });

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] = useFilterParams(
    TASK_HISTORY_FILTER_SPEC,
  );
  // `quickDate` chỉ là trạng thái của nút bấm nhanh, không phải bộ lọc gửi lên API — giữ ở
  // state cục bộ, không đưa lên URL (đúng luật 3: chỉ ghi thứ thực sự đổi kết quả).
  const [quickDate, setQuickDate] = useState(7);
  const filters = useMemo(
    () => ({
      employee_id: urlFilters.employee_id,
      status: urlFilters.status,
      from_date: urlFilters.from,
      to_date: urlFilters.to,
      quickDate,
    }),
    [urlFilters, quickDate],
  );

  const loadEmployees = useCallback(async () => {
    try {
      const res = await withBranch(employeeAPI.getEmployees, {
        page: 1,
        limit: 100,
        status: 'ACTIVE',
      });
      const list = res?.data || res?.items || res?.results || res || [];
      setEmployees(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  }, [withBranch]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        branch_id: selectedBranch?.id,
        employee_id: filters.employee_id || '',
        status: filters.status || '',
        from_date: filters.from_date || '',
        to_date: filters.to_date || '',
        page: page,
        limit: itemsPerPage,
      };

      const res = await withBranch(taskAPI.getShiftTasks, params);
      const payload = res.data || res;
      const list = payload.data || payload || [];
      const taskList = Array.isArray(list) ? list : [];
      const meta = payload.meta || res.meta || {};

      setTasks(taskList);
      setTotalItems(meta.total || taskList.length);

      // Tính thống kê từ dữ liệu hiện tại (có thể cần API riêng để tính tổng)
      const statsData = {
        total: meta.total || taskList.length,
        completed: taskList.filter((t) => t.status === 'COMPLETED').length,
        failed: taskList.filter((t) => t.status === 'FAILED').length,
        pending: taskList.filter((t) => t.status === 'PENDING').length,
        inProgress: taskList.filter((t) => t.status === 'IN_PROGRESS').length,
        needReview: taskList.filter((t) => t.status === 'NEED_REVIEW').length,
        reviewed: taskList.filter((t) => t.status === 'REVIEWED').length,
      };
      setStats(statsData);
    } catch (err) {
      console.error('Error loading task history:', err);
      setError('Không thể tải lịch sử task');
      toast.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch?.id, filters, page, itemsPerPage, withBranch]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [filters.employee_id, filters.status, filters.from_date, filters.to_date]);

  // Tên trường trong trang -> tên param trên URL.
  const URL_FIELD = {
    employee_id: 'employee_id',
    status: 'status',
    from_date: 'from',
    to_date: 'to',
  };

  const handleFilterChange = (field, value) => {
    setUrlFilters({ [URL_FIELD[field]]: value });
  };

  const handleQuickDateSelect = (days) => {
    setQuickDate(days);
    setUrlFilters({
      from: moment().subtract(days, 'days').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
    });
  };

  const handleResetFilters = () => {
    setQuickDate(7);
    setUrlFilters({
      employee_id: '',
      status: '',
      from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
      // #092536 — "Đặt lại" là về bộ lọc mặc định, gom luôn param deep-link theo cùng.
      shift_task_id: '',
    });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openDetailModal = (task) => {
    setSelectedTask(task);
    setShowDetailModal(true);
  };

  const openPreview = (url) => {
    if (!url) return;
    setPreviewUrl(url);
  };

  // #092536 — ?shift_task_id= tới từ tin "Giải trình mới — Cần duyệt" (phạt "Task quá hạn"):
  // tải ĐÚNG bản ghi bằng GET /shift-tasks/:id rồi mở modal Chi tiết Task, để người duyệt bấm
  // 1 cái là thấy task nào, hạn lúc nào, hệ thống ghi nhận quá hạn lúc nào. Tải lỗi/không thấy
  // thì báo bằng toast — KHÔNG im lặng để người bấm link tưởng trang hỏng.
  const deepLinkTaskId = urlFilters.shift_task_id;
  useEffect(() => {
    if (!deepLinkTaskId) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await taskAPI.getShiftTaskById(deepLinkTaskId);
        const task = res?.data ?? res;
        if (!alive) return;
        if (task && task.id) {
          setSelectedTask(task);
          setShowDetailModal(true);
        } else {
          toast.error('Không tìm thấy bản ghi Task theo đường dẫn');
        }
      } catch (err) {
        console.error('Error loading shift task from deep link:', err);
        if (alive) toast.error('Không thể tải chi tiết Task theo đường dẫn');
      }
    })();
    return () => {
      alive = false;
    };
  }, [deepLinkTaskId]);

  // Đóng modal thì gỡ `shift_task_id` khỏi URL (ghi `replace` của useFilterParams) — F5 còn
  // mở lại modal nếu người dùng CHƯA đóng, còn đã đóng mà param vẫn nằm lại thì lần mở sau
  // lại bật modal dù không ai bấm.
  const closeDetailModal = () => {
    setShowDetailModal(false);
    if (urlFilters.shift_task_id) setUrlFilters({ shift_task_id: '' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { bg: 'secondary', text: 'Chờ xử lý' },
      IN_PROGRESS: { bg: 'info', text: 'Đang làm' },
      COMPLETED: { bg: 'success', text: 'Hoàn thành' },
      FAILED: { bg: 'danger', text: 'Thất bại' },
      NEED_REVIEW: { bg: 'warning', text: 'Cần review' },
      REVIEWED: { bg: 'primary', text: 'Đã review' },
    };
    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
  };


  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Thống kê lịch sử Task</h1>
          </div>
        </div>

        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />

        {/* Thống kê tổng quan */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted mb-2">Tổng số</h6>
                <h3 className="mb-0">{stats.total}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-success">
              <Card.Body>
                <h6 className="text-muted mb-2">Thành công</h6>
                <h3 className="mb-0 text-success">{stats.completed}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-danger">
              <Card.Body>
                <h6 className="text-muted mb-2">Thất bại</h6>
                <h3 className="mb-0 text-danger">{stats.failed}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-warning">
              <Card.Body>
                <h6 className="text-muted mb-2">Đang xử lý</h6>
                <h3 className="mb-0 text-warning">{stats.pending + stats.inProgress + stats.needReview}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Bộ lọc */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Bộ lọc</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Nhân viên</Form.Label>
                  <Form.Select
                    value={filters.employee_id}
                    onChange={(e) => handleFilterChange('employee_id', e.target.value)}
                  >
                    <option value="">Tất cả nhân viên</option>
                    {employees.map((emp) => (
                      <option
                        key={emp.id}
                        value={emp.id}
                      >
                        {emp.full_name || emp.username}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Trạng thái</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    {TASK_STATUS_OPTIONS.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Chọn nhanh</Form.Label>
                  <Form.Select
                    value={filters.quickDate}
                    onChange={(e) => handleQuickDateSelect(Number(e.target.value))}
                  >
                    {QUICK_DATE_OPTIONS.map((opt) => (
                      <option
                        key={opt.days}
                        value={opt.days}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Từ ngày</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.from_date}
                    onChange={(e) => handleFilterChange('from_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Đến ngày</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.to_date}
                    onChange={(e) => handleFilterChange('to_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={loadData}
                disabled={loading}
              >
                {loading ? 'Đang tải...' : 'Tìm kiếm'}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleResetFilters}
              >
                Đặt lại
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Bảng danh sách */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Danh sách Task ({tasks.length})</h5>
              {loading && <span className="text-muted small">Đang tải...</span>}
            </div>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {!loading && tasks.length === 0 && !error && (
              <div className="text-center text-muted py-4">Không có task nào trong khoảng thời gian đã chọn.</div>
            )}
            {tasks.length > 0 && (
              <>
                <div className="table-responsive">
                  <Table
                    responsive
                    hover
                    striped
                  >
                    <thead>
                      <tr>
                        <th>Task</th>
                        <th>Nhân viên</th>
                        <th>Trạng thái</th>
                        <th>Hoàn thành</th>
                        <th>Reviewer</th>
                        <th>Kết quả review</th>
                        <th>QC Reviewer</th>
                        <th>Kết quả QC review</th>
                        <th>Điểm</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => {
                        const taskInfo = task.task || {};
                        const employeeName =
                          task.employee_name || task.employee?.full_name || task.employee?.username || '-';
                        const reviewerName =
                          task.reviewer_name || task.reviewer?.full_name || task.reviewer?.username || '-';
                        const qcReviewerName =
                          task.qc_reviewer?.full_name || task.qc_reviewer?.username || '-';
                        const points = task.actual_points ?? taskInfo.points ?? 0;
                        const taskType = taskInfo.task_type || 'SUB_TASK';
                        const qcReviewResult = task.qc_review_result || '-';

                        return (
                          <tr key={task.id}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                {taskInfo.name || taskInfo.id || '-'}
                                {taskType === 'MAIN_TASK' ? (
                                  <Badge bg="primary" className="ms-1">Việc trực ca</Badge>
                                ) : (
                                  <Badge bg="secondary" className="ms-1">Việc phải nghiệm thu</Badge>
                                )}
                              </div>
                              <small className="text-muted">{taskInfo.category || '-'}</small>
                            </td>
                            <td>{employeeName}</td>
                            <td>{getStatusBadge(task.status)}</td>

                            <td>
                              {task.completed_at ? (
                                <small>{moment(task.completed_at).format('DD/MM/YYYY HH:mm')}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{reviewerName}</td>
                            <td>
                              {task.review_result ? (
                                <Badge
                                  bg={
                                    task.review_result === 'PASS' || task.review_result === 'APPROVED'
                                      ? 'success'
                                      : task.review_result === 'FAIL' || task.review_result === 'REJECTED_AND_FIXED'
                                      ? 'danger'
                                      : 'warning'
                                  }
                                >
                                  {task.review_result}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {qcReviewerName !== '-' ? (
                                <div className="fw-semibold">{qcReviewerName}</div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {qcReviewResult !== '-' ? (
                                <Badge
                                  bg={
                                    qcReviewResult === 'APPROVED'
                                      ? 'success'
                                      : qcReviewResult === 'REJECTED_AND_FIXED'
                                      ? 'danger'
                                      : 'secondary'
                                  }
                                >
                                  {qcReviewResult}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {points !== 0 ? (
                                <Badge bg={points >= 0 ? 'success' : 'danger'}>
                                  {points >= 0 ? '+' : ''}
                                  {points}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openDetailModal(task)}
                              >
                                Chi tiết
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                {totalItems > itemsPerPage && (
                  <div className="mt-3">
                    <PaginationComponent
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      currentPage={page}
                      onPageChange={handlePageChange}
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Modal chi tiết task */}
        <Modal
          show={showDetailModal}
          onHide={closeDetailModal}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Chi tiết Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTask && (
              <>
                <div className="mb-3">
                  <strong>Task:</strong> {selectedTask.task?.name || '-'}
                </div>
                <div className="mb-3">
                  <strong>Nhân viên:</strong> {selectedTask.employee_name || selectedTask.employee?.full_name || '-'}
                </div>
                <div className="mb-3">
                  <strong>Trạng thái:</strong> {getStatusBadge(selectedTask.status)}
                </div>
                {/* #092536 — khối "lỗi" của phạt Task quá hạn: người duyệt tin "Giải trình mới"
                    phải thấy ngay HẠN lúc nào và AI/KHI NÀO ghi nhận quá hạn, không phải đi dò
                    tiếp màn khác. `deadline_penalized_at` do cron
                    penalizeExpiredTaskDeadlines() ghi (hệ thống tự động, không phải người bấm). */}
                {(selectedTask.deadline_at || selectedTask.deadline_penalized_at) && (
                  <div className="mb-3">
                    <hr />
                    <h6 className="mb-2">Thông tin quá hạn</h6>
                    {selectedTask.deadline_at && (
                      <div className="mb-2">
                        <strong>Hạn hoàn thành:</strong>{' '}
                        {moment(selectedTask.deadline_at).format('DD/MM/YYYY HH:mm')}
                      </div>
                    )}
                    {selectedTask.deadline_penalized_at ? (
                      <div className="mb-2">
                        <strong>Người ghi nhận quá hạn:</strong> Hệ thống (tự động){' '}
                        <span className="text-muted">
                          — lúc{' '}
                          {moment(selectedTask.deadline_penalized_at).format('DD/MM/YYYY HH:mm')}
                        </span>
                      </div>
                    ) : (
                      <div className="mb-2 text-muted">
                        Chưa có lượt ghi nhận quá hạn nào cho bản ghi này.
                      </div>
                    )}
                  </div>
                )}
                {selectedTask.task?.description && (
                  <div className="mb-3">
                    <strong>Mô tả:</strong> {selectedTask.task.description}
                  </div>
                )}
                {selectedTask.reviewer_name && (
                  <div className="mb-3">
                    <strong>Reviewer:</strong> {selectedTask.reviewer_name}
                  </div>
                )}
                {selectedTask.review_notes && (
                  <div className="mb-3">
                    <strong>Ghi chú review:</strong> {selectedTask.review_notes}
                  </div>
                )}

                {selectedTask.qc_reviewer && (
                  <div className="mb-3">
                    <hr />
                    <h6 className="mb-2">Thông tin QC Review</h6>
                    <div className="mb-2">
                      <strong>QC Reviewer:</strong>{' '}
                      {selectedTask.qc_reviewer.full_name || selectedTask.qc_reviewer.username || '-'}
                    </div>
                    {selectedTask.qc_reviewed_at && (
                      <div className="mb-2">
                        <strong>Thời gian QC review:</strong>{' '}
                        {moment(selectedTask.qc_reviewed_at).format('DD/MM/YYYY HH:mm')}
                      </div>
                    )}
                    {selectedTask.qc_review_result && (
                      <div className="mb-2">
                        <strong>Kết quả QC review:</strong>{' '}
                        <Badge
                          bg={
                            selectedTask.qc_review_result === 'APPROVED'
                              ? 'success'
                              : selectedTask.qc_review_result === 'REJECTED_AND_FIXED'
                              ? 'danger'
                              : 'secondary'
                          }
                        >
                          {selectedTask.qc_review_result}
                        </Badge>
                      </div>
                    )}
                    {selectedTask.qc_review_notes && (
                      <div className="mb-2">
                        <strong>Ghi chú QC review:</strong> {selectedTask.qc_review_notes}
                      </div>
                    )}
                    {Array.isArray(selectedTask.qc_review_video_urls) &&
                      selectedTask.qc_review_video_urls.length > 0 && (
                        <div className="mb-2">
                          <strong>Video QC review:</strong>
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {selectedTask.qc_review_video_urls.map((url, idx) => (
                              <div
                                key={idx}
                                className="position-relative"
                              >
                                <video
                                  controls
                                  style={{ width: 200, maxHeight: 150, borderRadius: 4 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(url, '_blank');
                                  }}
                                >
                                  <source
                                    src={url}
                                    type="video/mp4"
                                  />
                                  Trình duyệt không hỗ trợ video.
                                </video>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {Array.isArray(selectedTask.evidence_urls) && selectedTask.evidence_urls.length > 0 && (
                  <div className="mb-3">
                    <strong>Bằng chứng nhân viên:</strong>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {selectedTask.evidence_urls.map((url, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="btn p-0 border-0"
                          onClick={() => openPreview(url)}
                        >
                          <img
                            src={url}
                            alt={`evidence-${idx}`}
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(selectedTask.review_evidence_urls) && selectedTask.review_evidence_urls.length > 0 && (
                  <div className="mb-3">
                    <strong>Bằng chứng reviewer:</strong>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {selectedTask.review_evidence_urls.map((url, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="btn p-0 border-0"
                          onClick={() => openPreview(url)}
                        >
                          <img
                            src={url}
                            alt={`review-evidence-${idx}`}
                            style={{
                              width: '80px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTask.checklist_completed && (
                  <div className="mb-3">
                    <strong>Checklist:</strong>
                    <ul className="mt-2">
                      {Object.entries(selectedTask.checklist_completed).map(([key, value]) => (
                        <li key={key}>
                          {key}: {value ? '✓' : '✗'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeDetailModal}
            >
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal preview hình ảnh fullscreen */}
        <Modal
          show={Boolean(previewUrl)}
          onHide={() => setPreviewUrl(null)}
          size="lg"
          centered
        >
          <Modal.Body className="text-center">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="preview"
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </PageLayout>
  );
}
